import { Action, action, persist, Thunk, thunk } from 'easy-peasy'
import { NavigateFunction } from 'react-router-dom'
import csAPI from '../../../API/csConfig'
import { Color } from '../../../chess/types'
import { ColorSelection } from '../../../components/colorSelector/ColorSelector'
import { createQueryParams } from '../../../sharedComponents/src/globalHeader/common/queryParams'
import ReconnectingWebSocket from '../../../socket/ReconnectingWebsocket'
import { StoreModel } from '../../../store/store'
import {
    AIChallengeCreate,
    Bot,
    ChallengeEntry,
    ChallengeState,
    ConnectionStatus,
    GameResult,
    GameResultType,
    MMChallengeType,
    ScoreboardEntry,
    SearchOptions,
    TimeMode,
} from '../../../store/types'
import { getGameResultType } from '../../gameView/components/gameOverDialog/utils'
import { GameViewState, StateChangeRequest } from '../../gameView/GameViewModel'
import updateSelectedBotData from '../PlayBotsPanel/hooks/updateSelectedBotData'
import { MM_MESSAGES, MM_REQUESTS } from './MMTypes'

export const timeModeToString = (timeMode: TimeMode) =>
    `${timeMode.durationMinutes},${timeMode.clockIncrementSeconds},${timeMode.name}`

// const removeItem = (challenges, challenge, identifier) => {
//     const index = challenges.findIndex((item) => item[identifier] === challenge[identifier])
//     if (index !== -1) challenges.splice(index, 1)
// }

const requestKey = (requestType: string, id?: string) => `${requestType}:${id}`

// ----------------------------------------------------------------------------- type

export type MatchMakingConnectionModel = {
    socket: ReconnectingWebSocket | undefined
    connectionStatus: ConnectionStatus
    searchOptions: SearchOptions
    rematchMatchData: {
        pairId: string
        userName: string
    }
    rematchStatus?: StateChangeRequest
    // challengesToMe: ChallengeEntry[]
    // autoChallengesByMe: ChallengeEntry[]
    // customChallengesByMe: ChallengeEntry[]
    // directChallengesByMe: ChallengeEntry[]
    // openChallenges: ChallengeEntry[]
    challengesToMe: { [challengeId: string]: ChallengeEntry }
    autoChallengesByMe: { [challengeId: string]: ChallengeEntry }
    customChallengesByMe: { [challengeId: string]: ChallengeEntry }
    directChallengesByMe: { [pairId: string]: ChallengeEntry }
    openChallenges: { [challengeId: string]: ChallengeEntry }
    onlinePresence: string
    alreadyConnected: boolean
    gameInQueue: boolean
    // UI requests awaiting MM (key: tile time control or request type); MM replies carry no request id of ours
    pendingRequests: { [key: string]: { requestType: MM_REQUESTS; id?: string } }
    scoreboards: Array<ScoreboardEntry>
    bots: Array<Bot>
    isBotsLoading: boolean
    openCustomBotPanel: boolean
    // accept/reject/delete requests this tab sent, awaiting a reply; the MM reply carries no request id of ours
    sentRequests: { [key: string]: { message: string; retried: boolean } }

    setRematchMatchData: Action<MatchMakingConnectionModel, { pairId: string; userName: string }>
    setRematchStatus: Action<MatchMakingConnectionModel, StateChangeRequest | undefined>
    setSearchOptions: Action<MatchMakingConnectionModel, SearchOptions>
    setSocket: Action<MatchMakingConnectionModel, ReconnectingWebSocket | undefined>
    setConnectionStatus: Action<MatchMakingConnectionModel, ConnectionStatus>
    setChallenges: Action<
        MatchMakingConnectionModel,
        {
            challengesToMe?: ChallengeEntry[]
            challengesByMe?: ChallengeEntry[]
            isInitial?: boolean
        }
    >
    setOnlinePresence: Action<MatchMakingConnectionModel, string>
    setAlreadyConnected: Action<MatchMakingConnectionModel, boolean>
    setGameToQueue: Action<MatchMakingConnectionModel, boolean>
    setScoreboard: Action<MatchMakingConnectionModel, ScoreboardEntry>
    setBots: Action<MatchMakingConnectionModel, Array<Bot>>
    setStockfishBot: Action<MatchMakingConnectionModel>
    setOpenCustomBotPanel: Action<MatchMakingConnectionModel, boolean>
    setPendingRequest: Action<
        MatchMakingConnectionModel,
        { key: string; request?: MatchMakingConnectionModel['pendingRequests'][string] }
    >
    trackRequest: Thunk<
        MatchMakingConnectionModel,
        { key: string; requestType: MM_REQUESTS; id?: string },
        {},
        StoreModel,
        boolean
    >
    settlePendingRequests: Action<
        MatchMakingConnectionModel,
        { requestType?: string; messageType?: string; data?: any }
    >
    getGameFromQueue: Action<MatchMakingConnectionModel>
    sendAIChallenge: Action<
        MatchMakingConnectionModel,
        {
            strength: number
            color: ColorSelection
            time: number
            navigate: NavigateFunction
            increment?: number
            fen?: string
            challengeeId?: string
            useTimeControl?: boolean
        }
    >
    sendPlayerChallenge: Action<
        MatchMakingConnectionModel,
        {
            color: ColorSelection
            time: number
            increment?: number
            rated: boolean
            ratingFrom?: number
            ratingTo?: number
            FEN?: string
            challengeeId?: string
            challengeType: string
        }
    >
    sendRematchChallenge: Action<
        MatchMakingConnectionModel,
        {
            matchId: string
        }
    >
    sendDeleteChallenge: Action<MatchMakingConnectionModel, { challengeId: string; challengeType: MMChallengeType }>
    sendRejectChallenge: Action<MatchMakingConnectionModel, { pairId: string; isRematch: boolean }>
    acceptChallenge: Action<MatchMakingConnectionModel, { challengeType?: MMChallengeType; id: string }>
    getScore: Action<MatchMakingConnectionModel, { challengerId: string; challengeeId: string }>
    setSentRequest: Action<MatchMakingConnectionModel, { key: string; request?: { message: string; retried: boolean } }>
    settleSentRequest: Thunk<
        MatchMakingConnectionModel,
        { requestType: string; id?: string; retry: boolean },
        {},
        StoreModel,
        boolean
    >
    showGameOverDialog: Thunk<MatchMakingConnectionModel, undefined, {}, StoreModel>
    reset: Action<MatchMakingConnectionModel>
}

// ----------------------------------------------------------------------------- initial state

const matchMakingConnectionModel: MatchMakingConnectionModel = {
    socket: undefined,
    connectionStatus: ConnectionStatus.NOT_INITIALIZED,

    challengesToMe: {},
    autoChallengesByMe: {},
    customChallengesByMe: {},
    openChallenges: {},
    directChallengesByMe: {},
    searchOptions: {
        customChallenge: persist(
            {
                minutes: 5,
                increment: 0,
                rated: true,
                color: ColorSelection.RANDOM,
                fullRatingRange: false,
            },
            {
                storage: 'sessionStorage',
            },
        ),
        directChallenge: persist(
            {
                selectedChallenge: '3,0,blitz',
                rated: true,
                color: ColorSelection.RANDOM,
                customTime: false,
                minutes: 3,
                increment: 0,
            },
            {
                storage: 'sessionStorage',
            },
        ),
        computerChallenge: {
            selectedBot: undefined,
            selectedChallenge: '3,0,blitz',
            color: ColorSelection.RANDOM,
            strength: 5,
            customTime: false,
            minutes: 3,
            increment: 0,
            useTimeControl: true,
        },
    },
    rematchMatchData: {
        pairId: '',
        userName: '',
    },
    onlinePresence: '',
    alreadyConnected: false,
    gameInQueue: false,
    pendingRequests: {},
    scoreboards: [],
    bots: [],
    isBotsLoading: true,
    openCustomBotPanel: false,
    sentRequests: {},

    // ----------------------------------------------------------------------------- simple actions

    setSocket: action((state, socket) => {
        state.socket = socket
    }),

    setConnectionStatus: action((state, status) => {
        state.connectionStatus = status
    }),

    setChallenges: action((state, challengeObj) => {
        const { challengesByMe = [], challengesToMe = [], isInitial = true } = challengeObj

        challengesToMe?.forEach((challenge) => {
            if (!challenge.createdTime) {
                challenge.createdTime = Date.now()
            }
            switch (challenge.recordState) {
                case ChallengeState.ITEM_ADDED:
                    if (challenge.challengeType !== MMChallengeType.DIRECT) {
                        state.openChallenges[challenge.challengeId] = challenge
                    } else {
                        state.challengesToMe[challenge.pairId!] = challenge
                    }
                    break
                case ChallengeState.ITEM_REMOVED:
                    if (challenge.challengeType !== MMChallengeType.DIRECT) {
                        delete state.openChallenges[challenge.challengeId]
                    } else {
                        delete state.challengesToMe[challenge.pairId!]
                    }
                    break
                default:
            }
        })

        challengesByMe?.forEach((challenge) => {
            if (!challenge.createdTime) {
                challenge.createdTime = Date.now()
            }
            switch (challenge.recordState) {
                case ChallengeState.ITEM_ADDED:
                    if (challenge.challengeType === MMChallengeType.CUSTOM) {
                        state.customChallengesByMe[challenge.challengeId] = challenge
                    } else if (challenge.challengeType === MMChallengeType.DIRECT) {
                        state.directChallengesByMe[challenge.pairId!] = challenge
                    } else if (challenge.challengeType === MMChallengeType.AUTO && isInitial) {
                        state.autoChallengesByMe[challenge.challengeId] = challenge
                    }
                    break
                case ChallengeState.ITEM_REMOVED:
                    if (challenge.challengeType === MMChallengeType.CUSTOM) {
                        delete state.customChallengesByMe[challenge.challengeId]
                    } else if (challenge.challengeType === MMChallengeType.DIRECT) {
                        delete state.directChallengesByMe[challenge.pairId!]
                    } else if (challenge.challengeType === MMChallengeType.AUTO) {
                        delete state.autoChallengesByMe[challenge.challengeId]
                    }
                    break
                default:
            }
        })
    }),

    setSearchOptions: action((state, searchOptions) => {
        state.searchOptions = searchOptions
    }),

    setRematchMatchData: action((state, rematchMatchData) => {
        state.rematchMatchData = rematchMatchData
    }),
    setRematchStatus: action((state, rematchStatus) => {
        state.rematchStatus = rematchStatus
    }),
    setOnlinePresence: action((state, onlinePresence) => {
        state.onlinePresence = onlinePresence
    }),
    setAlreadyConnected: action((state, alreadyConnected) => {
        state.alreadyConnected = alreadyConnected
    }),
    setGameToQueue: action((state, haveGameInQueue) => {
        state.gameInQueue = haveGameInQueue
    }),
    setScoreboard: action((state, data) => {
        const existingScoreboard = state.scoreboards.find((item) => item.pairId === data.pairId)
        if (!!existingScoreboard) {
            state.scoreboards = state.scoreboards.map((item) => {
                if (item.pairId === data.pairId) {
                    return data
                }
                return item
            })
        } else {
            state.scoreboards.push(data)
        }
    }),
    setBots: action((state, data) => {
        state.bots = data
        if (data.length > 0) {
            const selectedBot = data[0]
            state.searchOptions = updateSelectedBotData(state.searchOptions, selectedBot)
        }
        state.isBotsLoading = false
    }),
    setStockfishBot: action((state) => {
        const bots = state.bots
        if (bots.length > 0) {
            const selectedBot = bots.find((bot) => bot.id === '00000000-0000-0000-0000-000000000006')
            if (selectedBot) {
                state.searchOptions = updateSelectedBotData(state.searchOptions, selectedBot)
            }
        }
    }),
    setOpenCustomBotPanel: action((state, value) => {
        state.openCustomBotPanel = value
    }),

    setPendingRequest: action((state, { key, request }) => {
        if (request) state.pendingRequests[key] = request
        else delete state.pendingRequests[key]
    }),

    // Returns false when `key` is already in flight. Unanswered after 10 s it is released with a busy notice.
    trackRequest: thunk((actions, { key, requestType, id }, { getState, getStoreActions }) => {
        if (getState().pendingRequests[key]) return false
        const request = { requestType, id }
        actions.setPendingRequest({ key, request })
        setTimeout(() => {
            if (getState().pendingRequests[key] !== request) return
            actions.setPendingRequest({ key })
            getStoreActions().setSnackbarMessage('Server is busy, please try again')
        }, 10000)
        return true
    }),

    settlePendingRequests: action((state, mData) => {
        const created = mData.messageType === MM_MESSAGES.CHALLENGE_CREATED && mData.data?.result
        const gameStarted =
            mData.messageType === MM_MESSAGES.MATCH_CREATED ||
            mData.messageType === MM_MESSAGES.CHALLENGE_ACCEPTED ||
            mData.messageType === MM_MESSAGES.PLAYER_IS_PLAYING
        Object.entries(state.pendingRequests).forEach(([key, { requestType, id }]) => {
            if (
                gameStarted ||
                mData.requestType === requestType ||
                // MM can answer a seek accept with a challengeCreate error
                (requestType === MM_REQUESTS.CHALLENGE_ACCEPT && mData.requestType === MM_REQUESTS.CHALLENGE_CREATE) ||
                (created?.challengeType === MMChallengeType.AUTO && id === timeModeToString(created.timeMode)) ||
                (mData.messageType === MM_MESSAGES.CHALLENGE_DELETED && id === mData.data?.rawData?.challengeId)
            ) {
                delete state.pendingRequests[key]
            }
        })
    }),

    // ----------------------------------------------------------------------------- socket actions

    getGameFromQueue: action((state) => {
        state.alreadyConnected = false
        if (state.socket === undefined) return
        state.socket.send(
            JSON.stringify({
                requestType: MM_REQUESTS.MY_STATUS,
            }),
        )
    }),

    sendAIChallenge: action((state, config) => {
        if (state.socket === undefined) return

        const challenge: AIChallengeCreate = {
            requestType: MM_REQUESTS.CHALLENGE_CREATE,
            rawData: {
                challengerColor: config.color,
                timeMode: {
                    durationMinutes: config.useTimeControl ? config.time : 0,
                    clockIncrementSeconds: config.useTimeControl && config.increment ? config.increment : 0,
                },
                matchKind: 'human_vs_ai',
                challengeeId: config.challengeeId,
                difficulty: {
                    level: config.strength,
                },
            },
        }
        if (config.fen) challenge.rawData.FEN = config.fen

        config.navigate('/')

        state.socket.send(JSON.stringify(challenge))
    }),

    acceptChallenge: action((state, payload) => {
        if (state.socket === undefined) return
        const message = JSON.stringify({
            requestType: MM_REQUESTS.CHALLENGE_ACCEPT,
            rawData: {
                [payload.challengeType === MMChallengeType.DIRECT ? 'pairId' : 'challengeId']: payload.id,
            },
        })
        state.socket.send(message)
        state.sentRequests[requestKey(MM_REQUESTS.CHALLENGE_ACCEPT, payload.id)] = { message, retried: false }
    }),

    sendPlayerChallenge: action((state, config) => {
        if (state.socket === undefined) return

        state.socket.send(
            JSON.stringify({
                requestType: MM_REQUESTS.CHALLENGE_CREATE,
                rawData: {
                    challengerColor: config.color,
                    timeMode: {
                        durationMinutes: config.time,
                        clockIncrementSeconds: config.increment ? config.increment : 0,
                    },
                    matchKind: 'human_vs_human',
                    rated: config.rated,
                    challengeeId: config.challengeeId,
                    challengeType: config.challengeType,
                    ratingFrom: config.ratingFrom,
                    ratingTo: config.ratingTo,
                },
            }),
        )
    }),

    sendRematchChallenge: action((state, config) => {
        if (state.socket === undefined) return
        state.socket.send(
            JSON.stringify({
                requestType: MM_REQUESTS.CHALLENGE_REMATCH,
                rawData: {
                    pairId: config.matchId,
                },
            }),
        )
    }),

    sendDeleteChallenge: action((state, payload) => {
        if (state.socket === undefined) return
        const message = JSON.stringify({
            requestType: MM_REQUESTS.CHALLENGE_DELETE,
            rawData: {
                challengeId: payload.challengeId,
                challengeType: payload.challengeType,
            },
        })
        state.socket.send(message)
        state.sentRequests[requestKey(MM_REQUESTS.CHALLENGE_DELETE, payload.challengeId)] = { message, retried: false }
    }),

    sendRejectChallenge: action((state, payload) => {
        if (state.socket === undefined) return
        const message = JSON.stringify({
            requestType: MM_REQUESTS.CHALLENGE_REJECT,
            rawData: {
                pairId: payload.pairId,
                isRematch: payload.isRematch,
            },
        })
        state.socket.send(message)
        state.sentRequests[requestKey(MM_REQUESTS.CHALLENGE_REJECT, payload.pairId)] = { message, retried: false }
    }),

    getScore: action((state, data) => {
        if (state.socket === undefined) return
        state.socket.send(
            JSON.stringify({
                requestType: MM_REQUESTS.CHALLENGE_SCORE,
                rawData: {
                    challengerId: data.challengerId,
                    challengeeId: data.challengeeId,
                },
            }),
        )
    }),

    setSentRequest: action((state, { key, request }) => {
        if (request) state.sentRequests[key] = request
        else delete state.sentRequests[key]
    }),

    // Called for every reply to accept/reject/delete. Returns true when the request was resent (once, jittered);
    // otherwise forgets it. Replies to requests this tab didn't send (other tabs) never cause a resend.
    settleSentRequest: thunk((actions, { requestType, id, retry }, { getState }) => {
        const key = requestKey(requestType, id)
        const sent = getState().sentRequests[key]
        if (!sent) return false
        if (!retry || sent.retried) {
            actions.setSentRequest({ key })
            return false
        }
        actions.setSentRequest({ key, request: { ...sent, retried: true } })
        setTimeout(() => getState().socket?.send(sent.message), 300 + Math.random() * 500)
        return true
    }),

    showGameOverDialog: thunk(async (actions, payload, { getStoreState, getStoreActions }) => {
        try {
            const { token, gameView } = getStoreState()
            if (gameView.gameState === GameViewState.GAME_RUNNING) {
                const response = await csAPI.get(`/api/history${createQueryParams({ lastRecord: true, token })}`)
                if (response.data) {
                    const d = response.data.matchHistory[0]
                    if (!!d) {
                        const storeActions = getStoreActions()
                        storeActions.gameView.setOpponentDisconnected(undefined)
                        storeActions.setDrawerOpen('closed')
                        storeActions.gameView.setGameEndEnabled(true)
                        storeActions.gameView.setCanClaimVictory(false)

                        const result: GameResult = {
                            winner: undefined,
                            type: GameResultType.Unknown,
                            newRating: {
                                player1_id: d.player1.id,
                                user1_rating: d.player1.finalRating,
                                player2_id: d.player2.id,
                                user2_rating: d.player2.finalRating,
                            },
                            outcome: d.outcome,
                            botdRewardURL: d.botdRewardURL,
                        }

                        // check the result who won ("0-1" is a fixed notation in chess!)
                        if (d.outcome === '1-0') result.winner = Color.White
                        else if (d.outcome === '0-1') result.winner = Color.Black

                        // decipher the reason
                        result.type = getGameResultType(d.reason)

                        // save game end result
                        storeActions.gameView.setGameResult(result)

                        // send game result to analytics
                        storeActions.gameView.sendGameResultToAnalytics({
                            reason: d.reason,
                            method: d.method,
                            newRating: result.newRating,
                        })

                        // clear match data
                        storeActions.gameView.setMatchData({
                            matchId: '',
                            gameServiceUrl: '',
                        })
                    }
                }
            }
        } catch (error) {
            console.error('Get last game from history error:', error)
        }
    }),

    reset: action((state) => {
        state.pendingRequests = {}
        state.challengesToMe = {}
        state.autoChallengesByMe = {}
        state.customChallengesByMe = {}
        state.openChallenges = {}
        state.directChallengesByMe = {}
        state.sentRequests = {}
        state.rematchMatchData = {
            pairId: '',
            userName: '',
        }
        state.socket = undefined
    }),
}

export default matchMakingConnectionModel
